import { z } from 'zod';

/** Consumer-safe quote row — no cost, margin, staff notes or purchase data (BR-SO-83). */
export const ConsumerQuoteListItemSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  workshopName: z.string().min(1),
  placeId: z.uuid(),
  version: z.number().int().positive(),
  status: z.enum(['SENT', 'PARTIALLY_APPROVED', 'APPROVED', 'REJECTED', 'EXPIRED']),
  sentAt: z.string().nullable(),
  validUntil: z.string().nullable(),
  totalCents: z.string().min(1),
  currency: z.string().min(1),
  vehiclePlate: z.string().nullable(),
  /** True when at least one line still lacks approve/reject. Chip Aguardando uses this, not status alone. */
  hasPendingCustomerDecision: z.boolean(),
});
export type ConsumerQuoteListItem = z.infer<typeof ConsumerQuoteListItemSchema>;

/** Consumer-facing list chips for Documentos → Orçamentos. */
export const ConsumerQuoteListStatusFilterSchema = z.enum([
  'all',
  'awaiting',
  'approved',
  'rejected',
  'expired',
]);
export type ConsumerQuoteListStatusFilter = z.infer<typeof ConsumerQuoteListStatusFilterSchema>;

export const ConsumerQuotesQuerySchema = z.object({
  status: ConsumerQuoteListStatusFilterSchema.optional(),
});
export type ConsumerQuotesQuery = z.infer<typeof ConsumerQuotesQuerySchema>;

export const ConsumerQuotesResponseSchema = z.object({
  items: z.array(ConsumerQuoteListItemSchema),
});
export type ConsumerQuotesResponse = z.infer<typeof ConsumerQuotesResponseSchema>;

export const ConsumerQuoteItemSchema = z.object({
  id: z.uuid(),
  itemType: z.string().min(1),
  description: z.string(),
  quantity: z.string().min(1),
  unitPriceCents: z.string().min(1).nullable(),
  totalCents: z.string().min(1),
  currency: z.string().min(1),
  approvedByCustomer: z.boolean(),
  rejectedByCustomer: z.boolean(),
});
export type ConsumerQuoteItem = z.infer<typeof ConsumerQuoteItemSchema>;

export const ConsumerQuoteDetailSchema = ConsumerQuoteListItemSchema.extend({
  items: z.array(ConsumerQuoteItemSchema),
  approvedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
});
export type ConsumerQuoteDetail = z.infer<typeof ConsumerQuoteDetailSchema>;

/** Ephemeral URL for the minimal quote PDF (BR-SO-84). */
export const ConsumerQuotePdfUrlSchema = z.object({
  downloadUrl: z.string().min(1),
  expiresAt: z.string().min(1),
  contentType: z.literal('application/pdf'),
  disposition: z.literal('inline'),
});
export type ConsumerQuotePdfUrl = z.infer<typeof ConsumerQuotePdfUrlSchema>;

/** Consumer quote line decision (BR-SO-92 / BR-SO-93). */
export const ConsumerQuoteItemDecisionSchema = z.enum(['APPROVED', 'REJECTED']);
export type ConsumerQuoteItemDecision = z.infer<typeof ConsumerQuoteItemDecisionSchema>;

export const ConsumerQuoteItemDecisionBodySchema = z.object({
  decision: ConsumerQuoteItemDecisionSchema,
});
export type ConsumerQuoteItemDecisionBody = z.infer<typeof ConsumerQuoteItemDecisionBodySchema>;

export const ConsumerQuoteItemDecisionsBodySchema = z.object({
  decisions: z
    .array(
      z.object({
        itemId: z.uuid(),
        decision: ConsumerQuoteItemDecisionSchema,
      }),
    )
    .min(1),
});
export type ConsumerQuoteItemDecisionsBody = z.infer<typeof ConsumerQuoteItemDecisionsBodySchema>;
